import { useCallback, useEffect, useState } from 'react';
import { reminderAPI } from '../lib/api';

export const REMINDER_CATEGORIES = ['training', 'grooming_reminder', 'style_reminder', 'posture', 'checkin'];

const defaultReminder = (category) => ({ category, frequency: 'daily', time: '09:00', enabled: false });

export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};

/**
 * Loads, edits, saves and test-sends reminder settings.
 * Keeps every category present so the UI always renders a full list.
 */
export const useReminders = () => {
  const [reminders, setReminders] = useState(REMINDER_CATEGORIES.map(defaultReminder));
  const [emailStatus, setEmailStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      const res = await reminderAPI.logs();
      setLogs(res.data.logs || []);
    } catch (err) {
      setLogs([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reminderAPI.get();
      const existing = res.data.reminders || [];
      setReminders(REMINDER_CATEGORIES.map(cat => existing.find(r => r.category === cat) || defaultReminder(cat)));
      setEmailStatus(res.data.email_status || null);
    } catch (err) {
      setReminders(REMINDER_CATEGORIES.map(defaultReminder));
    } finally {
      setLoading(false);
    }
    loadLogs();
  }, [loadLogs]);

  useEffect(() => { load(); }, [load]);

  const updateReminder = (index, field, value) => {
    setReminders(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await reminderAPI.save(reminders, getBrowserTimezone());
      if (res.data.email_status) setEmailStatus(res.data.email_status);
      return true;
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (category) => {
    setTesting(true);
    try {
      const res = await reminderAPI.sendTest(category);
      await loadLogs();
      return res.data;
    } finally {
      setTesting(false);
    }
  };

  return { reminders, emailStatus, logs, loading, saving, testing, updateReminder, save, sendTest, timezone: getBrowserTimezone() };
};
